import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { PageHeaderWrap, PageWrapper } from '../studentHome/styles';
import { Helmet } from 'react-helmet';
import { PageHeader } from '../../components';
import { GET_PAPERS_FOR_STUDENT_CLASS, SUBMIT_MISSING_COURSES_REPORT } from './graphql';
import { PageLoader } from '../../helpers';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';

/**
 * MissingAllocations
 *
 * This page allows students to report missing courses from their exam timetable.
 * - Fetches the list of courses for the student's class.
 * - Lets the student select courses that are missing from their allocation.
 * - Submits a report to the backend for further action.
 *
 * Features:
 * - Displays student index number and class (read-only).
 * - Shows a list of courses as checkboxes.
 * - Handles form submission with validation and feedback.
 * - Uses Apollo Client for GraphQL queries/mutations.
 * - Provides user feedback via react-toastify.
 */
export default function MissingAllocations() {
  // Get navigation state (indexNumber, studentClass) from React Router
  const location = useLocation();
  const { indexNumber, studentClass } = location.state;

  // State for selected course IDs
  const [selectedCourses, setSelectedCourses] = React.useState<string[]>([]);

  // GraphQL mutation for submitting missing courses report
  const [
    submitMissingCoursesReport,
    { data: submitData, loading: submitLoading, error: submitError },
  ] = useMutation(SUBMIT_MISSING_COURSES_REPORT);

  /**
   * Handles toggling a course checkbox.
   * Adds/removes the course ID from selectedCourses.
   */
  const toggleCourse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPaperId = e.target.value;
    if (selectedCourses.includes(selectedPaperId)) {
      setSelectedCourses((prev) => prev.filter((courseId) => courseId !== selectedPaperId));
    } else {
      setSelectedCourses((prev) => [...prev, selectedPaperId]);
    }
  };

  /**
   * Handles form submission.
   * Validates selection and submits the report via GraphQL mutation.
   * Shows success/warning/error toasts based on the result.
   */
  const submitReport = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedCourses.length === 0) {
      toast.error(`Select at least 1 course to submit report`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    submitMissingCoursesReport({ variables: { indexNumber, missingCourseIds: selectedCourses } })
      .then(({ data }) => {
        setSelectedCourses([]);
        console.log(data);
        if (data.submitMissingCoursesReport.status) {
          toast.success(data.submitMissingCoursesReport.message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.warn(data.submitMissingCoursesReport.message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          `Failed to submit report. Try again. If problem persists go to the exams office`,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      });
  };

  // Fetch the list of papers/courses for the student's class
  const { data, error, loading } = useQuery(GET_PAPERS_FOR_STUDENT_CLASS, {
    variables: { indexNumber, studentClass },
  });

  // Show loading spinner while fetching data
  if (loading) {
    return <PageLoader />;
  }

  // Show error toast if fetching fails
  if (error) {
    toast.error(
      `Error occured in fetching your schedule, 
         please try and login in again or visit exams office`,
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      },
    );
  }

  // Main render: form for reporting missing courses
  return (
    <PageWrapper>
      <Helmet>
        <title>KNUST, CoE Check timetable for Student</title>
        <meta
          name="check-timetable"
          content="Home page for KNUST,college of engineering students to check timetable schedule"
        />
      </Helmet>
      <PageHeaderWrap>
        <PageHeader />
      </PageHeaderWrap>
      <div className="w-full mt-8">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full mx-auto">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Report Missing Courses</h1>

          {/* Student Info */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-1">Index Number</label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              value={indexNumber}
              readOnly
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-1">Class</label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              value={studentClass}
              readOnly
            />
          </div>

          {/* Missing Courses Form */}
          <form action="#" onSubmit={submitReport}>
            <fieldset>
              <legend className="text-gray-700 font-semibold mb-2">Select Missing Courses</legend>

              <div className="space-y-2">
                {data.getPapersForStudentClass.map((paper: any) => (
                  <label
                    className={`flex items-center space-x-2 border rounded-md px-2 py-2 hover:cursor-pointer hover:bg-gray-100 hover:font-semibold ${
                      selectedCourses.includes(paper._id) &&
                      'bg-gray-100 border-gray-400 font-semibold'
                    }`}
                    key={paper._id}
                    htmlFor={paper._id}
                  >
                    <input
                      id={paper._id}
                      type="checkbox"
                      name="courses[]"
                      value={paper._id}
                      className="h-4 w-4 text-blue-600"
                      onChange={toggleCourse}
                      checked={selectedCourses.includes(paper._id)}
                    />
                    <span>
                      {paper.courseCode} - {paper.courseName}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="mt-6">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
              >
                {submitLoading ? <ClipLoader color="white" size={16} /> : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </PageWrapper>
  );
}
